/**
 * A short line of text shown beside a control while it is hovered or focused from the keyboard:
 * the name of an icon-only button, or why a button is disabled. `Button` and `IconButton` use it,
 * and so can any other control that needs a word of help.
 *
 * It follows WCAG 1.4.13: the pointer can move onto the text without it closing, because the text
 * sits inside the hovered wrapper, and Escape hides it without moving focus. Keyboard focus and a
 * tap on a touch screen show it at once. Hover waits 500ms so moving the pointer across a row of
 * icons does not flash a tip at each one, but a tip reached within 300ms of another closing opens at
 * once, so scanning the row is not a wait at each icon.
 *
 * The text is in the top layer (`popover="manual"`), so an `overflow: hidden` parent or a sticky
 * header never clips it, and `place` flips it below the control near the top of the viewport.
 * The text stays in the document while hidden, so a control that points `aria-describedby` at `id`
 * keeps its description.
 */
import * as stylex from "@stylexjs/stylex";
import type { KeyboardEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/layer.stylex";
import { layer } from "@lattice/tokens/layer.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import { place, followUntilClosed } from "./position.ts";
import { textStyle } from "./textStyle.ts";
const HOVER_DELAY_MS = 500;
const SCAN_WINDOW_MS = 300;

/* When any tooltip last closed, so the next one reached inside the scan window opens at once. */
let lastClosedAt = Number.NEGATIVE_INFINITY;
const styles = {
  bubble: {
    k1xSpc: "x1lliihq",
    kzqmXN: "xezivpi",
    ks0D6T: "xc5y1ld",
    k8WAf4: "x8hz100",
    kg3NbH: "xc7sml1",
    kaIpWk: "x8wwnw1",
    kWkggS: "x1h141q8",
    kMwMTN: "xxil687",
    k9WMMc: "x1yc453h",
    khDVqt: "xeaf4i8",
    $$css: true
  }
};
export interface TooltipProps {
  readonly id: string;
  readonly text: string;
  readonly children: ReactNode;
  /** Which edge of the control the tip lines up with. `end` for a control at the right of a row. */
  readonly align?: "start" | "end";
  /** Whether the control fills its row, so the wrapper must too. `"phone"` is below 640px only. */
  readonly fill?: boolean | "phone";
  /** Keeps the tip closed, for a control that only sometimes has something to say. */
  readonly off?: boolean;
}
export function Tooltip({
  id,
  text,
  children,
  align = "start",
  fill = false,
  off = false
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => cancel, []);
  useEffect(() => {
    const element = tip.current;
    const anchor = wrap.current;
    if (element === null || anchor === null || !open) return undefined;
    const settle = () => place(anchor, element, "top", align, 0);
    let stop = (): void => undefined;

    /*
      A tip often opens because focus just returned to its control as a menu or popover closed, and
      the browser refuses to show a popover while another one is still finishing its hide. One frame
      later it has finished.
    */
    const frame = requestAnimationFrame(() => {
      element.showPopover();
      settle();
      stop = followUntilClosed(settle);
    });

    /* On touch there is no Escape and a tap on something that takes no focus gives no blur, so a
       press anywhere outside the tip's control closes it. */
    const outside = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !anchor.contains(event.target)) {
        cancel();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("pointerdown", outside);
      cancelAnimationFrame(frame);
      stop();
      lastClosedAt = Date.now();
      if (element.matches(":popover-open")) element.hidePopover();
    };
  }, [open, align]);
  const show = () => {
    cancel();
    if (!off) setOpen(true);
  };
  const hide = () => {
    cancel();
    setOpen(false);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || !open) return;
    /* Escape closes the tip and nothing else, so a tip inside a dialog does not close the dialog. */
    event.stopPropagation();
    hide();
  };
  return <span ref={wrap} {...{
    0: {
      className: "x1n2onr6 x3nfvp2 x193iq5w"
    },
    2: {
      className: "x1n2onr6 x193iq5w x78zum5 xh8yej3"
    },
    1: {
      className: "x1n2onr6 x193iq5w xjjaqx5 x1t1oewe"
    },
    3: {
      className: "x1n2onr6 x193iq5w xjjaqx5 x1t1oewe"
    }
  }[!!(fill === true) << 1 | !!(fill === "phone") << 0]} onPointerEnter={() => {
    cancel();
    const wait = Date.now() - lastClosedAt < SCAN_WINDOW_MS ? 0 : HOVER_DELAY_MS;
    if (!off) timer.current = window.setTimeout(() => setOpen(true), wait);
  }}
  /* A finger lifting counts as leaving, so on touch only blur or Escape hides the tip. */ onPointerLeave={event => {
    if (event.pointerType !== "touch") hide();
  }}
  /* A touch screen has no hover, so a tap shows the tip until focus moves on. */ onPointerDown={event => {
    if (event.pointerType === "touch") show();
  }} onFocus={event => {
    if (event.target.matches(":focus-visible")) show();
  }} onBlur={hide} onKeyDown={onKeyDown}>
      {children}
      <span id={id} ref={tip} role="tooltip" popover="manual" className="xixxii4 x1anvkwx x1ghz6dp xc342km x8hz100 xnjsko4 x1rea2x4 xjbqb8w x1heor9g xq3i3c4">
        <span {...stylex.props(textStyle.label, styles.bubble)}>{text}</span>
      </span>
    </span>;
}